import { FormControl, MenuItem, Stack, TextField, Select, Checkbox, FormControlLabel, InputLabel, FormGroup, Card, Divider, Button, ButtonGroup, Paper, Typography, Tabs } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGetProductByIdQuery, useUpdateSKUMutation } from '../features/products/productSlice';
import Loading from '../components/Loading';
import { SingleProductTypeSelect } from '../components/ProductAutocomplete';
import ProductMetadataEditor from '../components/ProductMetadataView';
import { useWindowSize } from '../helpers/window';
import ProductImageView from '../components/ProductImageView';
import { useGetSKUMetadataQuery } from '../features/products/metadataSlice';
import { capitalized } from '../helpers/strings';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';

const ProductDetailsCard = ({product}) => {
    const { data: metadatas, error: metadatasError, isLoading: metadatasAreLoading} = useGetSKUMetadataQuery(product ? product.id : null, {skip: !product});

    if (metadatasAreLoading || !metadatas) {
        return <Loading />;
    }
    if (metadatasError) {
        return <div>Error: {metadatasError}</div>
    }

    var elements = [];
    var md_per_key = {};

    metadatas.objects.forEach((md) => {
        md_per_key[md.key] = md;
    });

    var idx = 1;

    const infoFields = [
        {"type": "sku", "key": "name", "style": "h2"},
        {"type": "metadata", "key": "aliases", "label": "Alias Names", "lookup": "svalue"},
        {"type": "metadata", "key": "colors", "label": "Colors Available", "lookup": "svalue"},
        {"type": "metadata", "key": "width", "label": "Dimensions", "caption": "Width", "lookup": "fvalue", "unit": "in"},
        {"type": "metadata", "key": "height", "caption": "Height", "lookup": "fvalue", "unit": "in"},
        {"type": "metadata", "key": "depth", "caption": "Depth", "lookup": "fvalue", "unit": "in"},
        {"type": "metadata", "key": "material", "label": "Material", "lookup": "svalue"},
        {"type": "metadata", "key": "weight", "label": "Weight", "unit": "oz", "lookup": "fvalue"},
        {"type": "metadata", "key": "laser_settings", "label": "Laser Settings", "lookup": "svalue"},
        {"type": "metadata", "key": "placement_options", "label": "Engraving Placement Options", "lookup": "svalue"},
        {"type": "metadata", "key": "design_options", "label": "Font/Design Options", "lookup": "svalue"},
        {"type": "metadata", "key": "platforms", "label": "Platforms", "lookup": "svalue"},
        {"type": "metadata", "key": "pricing", "label": "Pricing", "lookup": "svalue"},
        {"type": "metadata", "key": "notes", "label": "Exceptions/Notes", "lookup": "svalue", "parse": "markdown"},
        {"type": "metadata", "key": "location", "label": "Shelf Location", "lookup": "svalue"},
    ];

    infoFields.forEach((field) => {
        let fieldType = field.type;
        var caption = field.caption ? <b>{field.caption}: </b> : "";
        var unit = field.unit ? " " + field.unit : "";
        var value = null;

        if (fieldType === "sku") {
            value = product[field.key];
        } else if (fieldType === "metadata") {
            let md_key = field.key
            let md_kind = field.lookup;
            if (md_key in md_per_key) {
                value = capitalized(md_per_key[md_key][md_kind].toString());
            }
        }
        var noprewrap = false;
        var labelKey = `label_${idx}`;
        var elementKey = `element_${idx}`;

        if (value) {
            if(field.parse) {
                if(field.parse === "markdown") {
                    value = <ReactMarkdown key="valueMD" components={{
                        h4: ({node, ...props}) => <h4 style={{marginBlock: 0}} {...props} />,
                        p: ({node, ...props}) => <p style={{marginBlock: 0}} {...props} />,
                        ul: ({node, ...props}) => <ul style={{marginBlock: 0}} {...props} />
                    }}>{value}</ReactMarkdown>;
                    noprewrap = true;
                }
            }
            if(field.label) {
                elements.push(<Typography key={labelKey} variant="h3">{field.label}</Typography>);
            }
            if(field.style) {
                if(field.style === "h2") {
                    elements.push(<Typography key={labelKey} variant="h2">{caption}{value}</Typography>)
                }
            } else {
                let variant = field.style ? field.style : "div";
                if(noprewrap)
                    elements.push(<Typography key={elementKey} variant={variant} style={{margin: 0, paddingLeft: 10}}>{caption}{value}{unit}</Typography>)
                else
                    elements.push(<Typography key={elementKey} variant={variant} style={{margin: 0, whiteSpace: "pre-wrap", paddingLeft: 10}}>{caption}{value}{unit}</Typography>)
            }
        }
        idx += 1;
    });

    return <Stack sx={{marginTop: 2, marginBottom: 4}} spacing={2}>
        <ProductImageView product={product} width={300} height={300} nocard={true} nolabel={true} fit="contain" />
        {elements}
    </Stack>
}

const ProductView = () => {
    let params = useParams();

    const {data: product, error, isLoading } = useGetProductByIdQuery(params.productId)

    if (isLoading || !product) {
        return <Loading />;
    }
    if (error) {
        return <div>Error: {error}</div>
    }

    return <Stack sx={{marginTop: 2, marginBottom: 4}} spacing={2}>
        <ButtonGroup>
            <Button component={Link} to={`/products/${product.product_family ?? '_others'}`}>Back</Button>
            <Button variant="contained" component={Link} to={`/products/${product.product_family}/${product.id}/edit`}>Edit</Button>
        </ButtonGroup>
        <ProductDetailsCard product={product} />
    </Stack>

}

export default ProductView;
export {ProductDetailsCard};
